import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import useSWR from 'swr';
import useUnitContext from '/@/core/context/unit';
import { getZoneByDate } from '/@/core/dates';
import { _ } from '/@/locale';
import { getRecommendationByIdFromAPI } from '/@/store/private-data/recommendations/apiProviders';
import { getSessionCountry } from '/@/store/session/selectors';
import type { Recommendation as RecommendationType } from '/@/types/recommendation';
import { capitalizeFirstLetter } from '/@/utils';
import { reverse } from '/@/utils/router';
import { recommendationResultUrl } from '/@/views/recommendations/seed-recommendation-route';
import { useZones } from '../zones';

export interface RecommendationItem {
    id: string;
    crop: 'corn' | 'soy';
    typeLabel: string;
    isCropProtection: boolean;
    created: string;
    plantingDate?: string;
    fieldName: string;
    field_id: string;
    environment: string;
    product: string[];
    backup: any;
    status: string;
    isOldRecommendation: boolean;
    iconLabel: string;
    icon:
        | 'cornRecommendation'
        | 'soyRecommendation'
        | 'weedRecommendation'
        | 'cornDensityRecommendation'
        | 'weedV2'
        | 'diseaseRecommendation'
        | 'plagueRecommendation'
        | 'seedBag';
    type: 'hybrid' | 'weed' | 'density' | 'cropProtection';
    /** Opens this recommendation. Each type resolves to its own view, so callers delegate here. */
    onClick?: (row: RecommendationItem, options?: { openComments?: boolean }) => void;
}

export const useProcessRecommendation = () => {
    const navigate = useNavigate();
    const units = useUnitContext();
    const country = useSelector(getSessionCountry);

    const cornZonesQuery = useZones({ type: 'corn' });
    const soyZonesQuery = useZones({ type: 'soy' });
    const zones = useMemo(
        () => [...(cornZonesQuery.data || []), ...(soyZonesQuery.data || [])],
        [cornZonesQuery.data, soyZonesQuery.data]
    );

    return useCallback(
        (reco: RecommendationType): RecommendationItem => {
            let recoData;
            let isCropProtection = false;
            let productNames = [];
            let typeLabel = '';
            let key = '';
            let cropType = '';
            let options = [];
            let icon: RecommendationItem['icon'] = '';
            let iconLabel: string;
            let onClick: (reco: any, options?: { openComments?: boolean }) => void;
            let hasShare = false;
            let type: RecommendationItem['type'] = '';

            // Determine crop and protection type
            if (reco.crops.length) {
                recoData = reco.crops[0];
                productNames = reco?.crops?.[0]?.[`${recoData?.crop}ProductsNames`];
                typeLabel = capitalizeFirstLetter(recoData.crop);
                cropType = recoData.crop;
                type = 'hybrid';
                if (cropType === 'corn') {
                    icon = 'cornRecommendation';
                    iconLabel = _('Híbrido');
                } else if (cropType === 'soy') {
                    icon = 'soyRecommendation';
                    iconLabel = _('Híbrido');
                }
                onClick = (row, options) => {
                    navigate(
                        recommendationResultUrl({
                            recommendationId: row.id,
                            crop: row.typeLabel.toLowerCase(),
                            qs: options?.openComments ? { openComments: 'true' } : undefined,
                        })
                    );
                };
                hasShare = true;
            } else if (reco.cornDensity) {
                recoData = reco.cornDensity;
                productNames = [reco.cornDensity.productProfileName];
                typeLabel = 'Corn Density';
                cropType = 'corn';
                icon = 'cornDensityRecommendation';
                iconLabel = _('Densidad');
                type = 'density';
                onClick = (row, options) => {
                    navigate(
                        reverse('recommendations.CornDensityRecommendation', {
                            params: { recommendationId: row.id },
                            qs: options?.openComments ? { openComments: 'true' } : undefined,
                        })
                    );
                };
            } else if (reco.weedV2) {
                recoData = reco.weedV2;
                isCropProtection = true;
                key = 'weedV2';
                icon = key;
                type = 'cropProtection';
                iconLabel = _('Herbicida');
            } else if (reco.diseaseRecommendation) {
                isCropProtection = true;
                recoData = reco.diseaseRecommendation;
                key = 'diseaseRecommendation';
                icon = key;
                type = 'cropProtection';
                iconLabel = _('Fungicida');
            } else if (reco.plagueRecommendation) {
                isCropProtection = true;
                recoData = reco.plagueRecommendation;
                key = 'plagueRecommendation';
                icon = key;
                type = 'cropProtection';
                iconLabel = _('Insecticida');
            } else if (reco.seedTreatmentRecommendation) {
                isCropProtection = true;
                recoData = reco.seedTreatmentRecommendation;
                key = 'seedTreatmentRecommendation';
                icon = 'seedBag';
                type = 'cropProtection';
                iconLabel = _('Tratamiento de semillas');
            } else if (
                reco.backup &&
                typeof reco.backup === 'object' &&
                (reco.backup as RecommendationType).seedTreatmentRecommendation
            ) {
                isCropProtection = true;
                recoData = (reco.backup as RecommendationType).seedTreatmentRecommendation;
                key = 'seedTreatmentRecommendation';
                icon = 'seedBag';
                type = 'cropProtection';
                iconLabel = _('Tratamiento de semillas');
            }

            // Every crop-protection row opens the same detail route, whether or not the stored
            // `result` is there -- rows are clicked to *see* what's in them, so gating the
            // handler on `result` would leave older rows inert (the mobile card has nothing
            // else to fall back to).
            if (isCropProtection) {
                onClick = (row, options) => {
                    navigate(
                        reverse('cropProtectionRecommendationDetail', {
                            params: {
                                recommendationId: row.id,
                            },
                            qs: options?.openComments ? { openComments: 'true' } : undefined,
                        })
                    );
                };
            }

            // The summary columns and the share action, on the other hand, are derived from
            // `result` and have nothing to show without it.
            if (isCropProtection && recoData?.result) {
                cropType = recoData.result.crop;
                options = reco?.[key]?.result?.options ?? [];
                typeLabel = reco?.[key]?.result?.options?.[0]?.type ?? '';
                productNames = options
                    .map((option: any) =>
                        option.columns.map((column: any) =>
                            column.products.map((productConfig: any) => productConfig.product.name)
                        )
                    )
                    .flat(2);
                hasShare = true;
            }

            // Get zone information
            const filteredZones = zones.filter((z) => z.country === country.code);
            const plantingDate = recoData?.plantingDate;
            const zone = cropType && plantingDate ? getZoneByDate(filteredZones, cropType, plantingDate) : undefined;

            return {
                id: reco.id,
                crop: cropType,
                typeLabel: typeLabel,
                isCropProtection,
                created: reco.created ? reco.created.split('T')?.[0]?.replaceAll('-', '/') : '',
                plantingDate: plantingDate?.replaceAll('-', '/'),
                fieldName: reco.fieldName,
                field_id: reco.field,
                environment:
                    isCropProtection || !recoData?.environment
                        ? ''
                        : units.toYield(recoData.environment, cropType).toString(),
                product: productNames,
                backup: reco.backup,
                status: reco.status,
                isOldRecommendation: !zone || !reco.backup,
                icon,
                iconLabel: iconLabel,
                onClick,
                hasShare,
                type: type,
            };
        },
        [country.code, units, zones]
    );
};

export const useRecommendationById = (recoId: number) =>
    useSWR(recoId ? { key: 'myRecommendation', recoId } : null, getRecommendationByIdFromAPI, {});
